import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import config from "../../config";
import { formatApiError } from "../../utilities/apiErrorMessages";
import { coalesceAsync, invalidateCoalescedAsync } from "../../utilities/coalesceAsync";
const COLORS_LIST_CACHE_KEY = "get-colors:";

export const GetColors = createAsyncThunk("color/GetColors", async (obj, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI
    try {
        // Support two calling styles: GetColors(token) or GetColors({ token, search, page, per_page })
        let token = null;
        let params = {};
        if (typeof obj === 'string' || obj instanceof String) {
            token = obj;
        } else if (obj && typeof obj === 'object') {
            token = obj.token;
            params = { ...obj };
            delete params.token;
        }

        const qs = Object.entries(params)
            .filter(([, v]) => v !== undefined && v !== null && v !== '')
            .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
            .join('&');
        const url = `${config.apiUrl}/colors${qs ? `?${qs}` : ''}`;

        const data = await coalesceAsync(
            `get-colors:${url}`,
            async () => {
                const response = await fetch(url, {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`
                    }
                })

                const body = await response.json();

                if (!response.ok) {
                    const error = new Error(body?.message ?? "Unable to fetch colors");
                    error.payload = body;
                    throw error;
                }

                return body;
            },
            { ttlMs: 1500 }
        );

        return fulfillWithValue(data)

    } catch (error) {
        if (error?.payload) {
            return rejectWithValue(error.payload)
        }
        return rejectWithValue({ message: error?.message ?? "Unable to fetch colors" })
    }
})

export const AddColor = createAsyncThunk("color/AddColor", async (obj, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI
    try {
        const response = await fetch(`${config.apiUrl}/colors`, {
            method: "POST",
            body: JSON.stringify(obj.values),
            headers: {
                'Accept': "application/json",
                "Content-Type": "application/json",
                'Authorization': `Bearer ${obj.token}`
            }

        })

        const data = await response.json();

        if (!response.ok) {
            return rejectWithValue(data)
        } else {
                        invalidateCoalescedAsync(COLORS_LIST_CACHE_KEY)
            return fulfillWithValue(data)
        }


    } catch (error) {
        throw rejectWithValue(error.message)
    }
})


export const UpdateColor = createAsyncThunk("color/UpdateColor", async (obj, thunkAPI) => {
                invalidateCoalescedAsync(COLORS_LIST_CACHE_KEY)
    const { rejectWithValue, fulfillWithValue } = thunkAPI
    try {
        const response = await fetch(`${config.apiUrl}/colors/${obj.id}`, {
            method: "PUT",
            body: JSON.stringify(obj.values),
            headers: {
                'Accept': "application/json",
                "Content-Type": "application/json",
                'Authorization': `Bearer ${obj.token}`
            }

        })

        const data = await response.json();

        if (!response.ok) {
            return rejectWithValue(data)
        } else {
            return fulfillWithValue(data)
        }

    } catch (error) {
        throw rejectWithValue(error.message)
    }
})


export const DeleteColor = createAsyncThunk("color/DeleteColor", async (obj, thunkAPI) => {
        invalidateCoalescedAsync(COLORS_LIST_CACHE_KEY)
    const { rejectWithValue, fulfillWithValue } = thunkAPI
    try {
        const response = await fetch(`${config.apiUrl}/colors/${obj.id}`, {
            method: "DELETE",
            headers: {
                'Accept': "application/json",
                "Content-Type": "application/json",
                'Authorization': `Bearer ${obj.token}`
            }

        })

        const data = response.status === 204 ? {} : await response.json();
        if (!response.ok) {
            return rejectWithValue(data)
        }
        return fulfillWithValue({
            id: obj.id,
            message: data?.message ?? null,
        })
    } catch (error) {
        throw rejectWithValue(error.message)
    }
})

export const ColorSlice = createSlice({
    name: "color",
    initialState: {
        colors: [],
        error: null,
        successMessage: null,
        loading: false,
        selectedColor: null
    },
    reducers: {
        selectColor: (state, action) => {
            state.selectedColor = action.payload
        },
        clearColorFeedback: (state) => {
            state.error = null;
            state.successMessage = null;
        }
    },

    extraReducers: {
        [GetColors.pending]: (state, action) => {
            state.loading = true
            state.successMessage = null
            state.error = null
        },
        [GetColors.fulfilled]: (state, action) => {
            state.loading = false
            state.colors = Array.isArray(action.payload.data) ? action.payload.data : []
            state.successMessage = null
            state.error = null
        },

        [GetColors.rejected]: (state, action) => {

            state.loading = false
            state.successMessage = null
            state.error = formatApiError(action.payload, 'Something went wrong')
        },

        [AddColor.pending]: (state, action) => {
            state.loading = true
            state.error = null
            state.successMessage = null
        },

        [AddColor.fulfilled]: (state, action) => {
            state.loading = false
            state.error = null
            const created = action.payload.data;
            if (created) {
                state.colors = [...(state.colors ?? []), created]
            }
            state.successMessage = action.payload.message
        },

        [AddColor.rejected]: (state, action) => {
            state.loading = false
            state.error = formatApiError(action.payload, 'Something went wrong')
            state.successMessage = null
        },

        [UpdateColor.pending]: (state, action) => {
            state.loading = true
            state.error = null
            state.successMessage = null
        },
        [UpdateColor.fulfilled]: (state, action) => {

            state.loading = false
            const index = state.colors.findIndex(color => color.id === action.payload.data.id);
            state.colors[index] = {
                ...state.colors[index],
                ...action.payload.data,
            };
            state.successMessage = action.payload.message
            state.error = null
        },

        [UpdateColor.rejected]: (state, action) => {
            state.loading = false
            state.successMessage = null
            state.error = formatApiError(action.payload, 'Something went wrong')
        },

        //DeleteColor actions

        [DeleteColor.pending]: (state, action) => {
            state.loading = true
            state.error = null
            state.successMessage = null
        },
        [DeleteColor.fulfilled]: (state, action) => {
            state.loading = false
            state.error = null
            state.successMessage = action.payload?.message ?? null
            const id = action.payload?.id
            if (id != null) {
                state.colors = (state.colors ?? []).filter((color) => color.id !== id)
            }
        },
        [DeleteColor.rejected]: (state, action) => {
            state.loading = false
            state.error = formatApiError(action.payload, 'Something went wrong')
            state.successMessage = null
        },
    }
})

export default ColorSlice.reducer;
export const { selectColor, clearColorFeedback } = ColorSlice.actions
